import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";
import "./User.css";
import PermissionModal from "../components/PermissionModal";

//const API = import.meta.env.VITE_API_URL;
const MAX_PWD_LEN = 30;

const emptyForm = {
  id: null,
  usertype: "Member",
  username: "",
  uid: "",
  email: "",
  pwd: "",
  userstatus: "active",
};

export default function User() {
  const navigate = useNavigate();
  const { logout, hasPermission } = useAuth();

  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admins: 0,
    members: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  /* ================= FILTER STATE ================= */
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  /* ================= SORT STATE ================= */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ================= FORM STATE ================= */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const isEdit = !!form.id;
  /* =================PERMISSION MODAL ================= */
  const [permUser, setPermUser] = useState(null);

  /* ================= AUTH HEADER (UNCHANGED) ================= */
  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ================= LOAD USERS ================= */
  const loadUsers = async () => {
    try {
      const res = await apiFetch(
        `/users`,
        { headers: authHeader() },
        logout,
        navigate
      );
      setUsers(await res.json());
    } catch (err) {
      setError(err.message || "Unable to load users");
    }
  };

  const loadSummary = async () => {
    try {
      const res = await apiFetch(
        `/users/summary`,
        { headers: authHeader() },
        logout,
        navigate
      );
      if (res.ok) setSummary(await res.json());
    } catch {}
  };

  useEffect(() => {
    setShowForm(false);
    setForm({ ...emptyForm });

    Promise.all([loadUsers(), loadSummary()]).finally(() => setLoading(false));
  }, []);

  /* ================= SORT HANDLER ================= */
  const handleSort = (key) => {
    setSortDir((prev) =>
      sortKey === key ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setSortKey(key);
  };

  /* ================= FILTER + SORT ================= */
  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();

    let data = users.filter((u) => {
      const matchesSearch =
        u.username?.toLowerCase().includes(q) ||
        u.uid?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q);

      const matchesType = filterType === "All" || u.usertype === filterType;
      const matchesStatus =
        filterStatus === "All" || u.userstatus === filterStatus;

      return matchesSearch && matchesType && matchesStatus;
    });

    if (sortKey) {
      data = [...data].sort((a, b) => {
        const x = (a[sortKey] ?? "").toString().toLowerCase();
        const y = (b[sortKey] ?? "").toString().toLowerCase();
        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [users, search, filterType, filterStatus, sortKey, sortDir]);

  /* ================= FORM HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pwd" && value.length > MAX_PWD_LEN) return;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUser = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(true);
  };

  const handleEditUser = (u) => {
    if (!hasPermission("user", "update")) return;

    setForm({
      id: u.id,
      usertype: u.usertype,
      username: u.username,
      uid: u.uid,
      email: u.email,
      pwd: "",
      userstatus: u.userstatus,
    });

    setFormError("");
    setShowForm(true);
  };
  const resetForm = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(false);
  };

  const submit = async () => {
    const method = isEdit ? "PUT" : "POST";
    const url = isEdit ? `/users/${form.id}` : `/users`;

    const payload = {
      usertype: form.usertype,
      username: form.username,
      uid: form.uid,
      email: form.email,
      userstatus: form.userstatus,
      createdbyuid: localStorage.getItem("uid"),
    };

    if (form.pwd.trim()) payload.pwd = form.pwd;

    try {
      const res = await apiFetch(
        url,
        {
          method,
          headers: authHeader(),
          body: JSON.stringify(payload),
        },
        logout,
        navigate
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      resetForm();
      loadUsers();
      loadSummary();
    } catch (err) {
      setFormError(err.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!hasPermission("user", "delete")) return;

    if (!window.confirm("Delete this user?")) return;

    await apiFetch(
      `/users/${id}`,
      { method: "DELETE", headers: authHeader() },
      logout,
      navigate
    );

    loadUsers();
    loadSummary();
  };

  const openPermissions = (u) => {
    if (!hasPermission("user", "update")) return;
    setPermUser(u);
  };

  /* ================= UI ================= */
  return (
    <div className="user-page">
      {/* ===== SUMMARY ===== */}
      <div className="user-cards">
        <div className="card">
          Total Users <span>{summary.total}</span>
        </div>
        <div className="card">
          Active Users <span>{summary.active}</span>
        </div>
        <div className="card">
          Inactive Users <span>{summary.inactive}</span>
        </div>
        <div className="card">
          Admin Users <span>{summary.admins}</span>
        </div>
        <div className="card">
          Member Users <span>{summary.members}</span>
        </div>
      </div>

      {/* ===== TOOLBAR ===== */}
      <div className="user-filters">
        <input
          placeholder="Search by UID, name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="Admin">Admin</option>
          <option value="Member">Member</option>
          <option value="Guest">Guest</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <button
          className="add-user-btn"
          onClick={handleAddUser}
          disabled={!hasPermission("user", "create")}
          title={!hasPermission("user", "create") ? "No permission" : ""}
        >
          + Add User
        </button>
      </div>

      {/* ===== INLINE FORM (RESTORED POSITION) ===== */}
      {showForm && (
        <div className="user-inline-form">
          <div className="form-row">
            <select
              name="usertype"
              value={form.usertype}
              onChange={handleChange}
            >
              <option>Admin</option>
              <option>Member</option>
              <option>Guest</option>
            </select>

            <input
              name="uid"
              placeholder="UID"
              value={form.uid}
              onChange={handleChange}
            />
            <input
              name="username"
              placeholder="Username"
              value={form.username}
              onChange={handleChange}
            />
            <input
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
            />
            <input
              type="password"
              name="pwd"
              placeholder="Password"
              value={form.pwd}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <select
              name="userstatus"
              value={form.userstatus}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <button className="save-btn" onClick={submit}>
              Save
            </button>
            <button className="cancel-btn" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ===== TABLE ===== */}
      {!loading && (
        <div className="user-table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("usertype")}>Type</th>
                <th onClick={() => handleSort("uid")}>UID</th>
                <th onClick={() => handleSort("username")}>Name</th>
                <th onClick={() => handleSort("email")}>Email</th>
                <th onClick={() => handleSort("userstatus")}>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.usertype}</td>
                  <td>
                    <input
                      className="uid-cell"
                      type="text"
                      value={u.uid || ""}
                      readOnly
                      tabIndex={0}
                      aria-label="UID"
                    />
                  </td>

                  <td>
                    <input
                      className="username-cell"
                      type="text"
                      value={u.username || ""}
                      readOnly
                      tabIndex={0}
                      aria-label="Username"
                    />
                  </td>

                  <td>
                    <input
                      className="email-cell"
                      type="text"
                      value={u.email || ""}
                      readOnly
                      tabIndex={0}
                      aria-label="Email"
                    />
                  </td>

                  <td>
                    <span className={`status ${u.userstatus}`}>
                      {u.userstatus}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditUser(u)}
                        disabled={!hasPermission("user", "update")}
                        title={
                          !hasPermission("user", "update")
                            ? "No permission"
                            : ""
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() => del(u.id)}
                        disabled={!hasPermission("user", "delete")}
                        title={
                          !hasPermission("user", "delete")
                            ? "No permission"
                            : ""
                        }
                      >
                        Delete
                      </button>

                      <button
                        className="edit-btn"
                        onClick={() => openPermissions(u)}
                        disabled={!hasPermission("user", "update")}
                        title={
                          !hasPermission("user", "update")
                            ? "No permission"
                            : ""
                        }
                      >
                        Permissions
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredUsers.length && (
                <tr>
                  <td colSpan="6" className="no-data">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <PermissionModal
            open={!!permUser}
            user={permUser}
            onClose={() => setPermUser(null)}
          />
        </div>
      )}

      {error && <p className="form-error">{error}</p>}
      {formError && <p className="form-error">{formError}</p>}
    </div>
  );
}
